/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import PriceRangeInput from '@/components/price-range-input';
import { usePriceRangeValidation, validatePriceRange } from '@/hooks/use-price-range-validation';
import DefaultRefinement from '../refine-default';
import type { RefinementProps } from '../types';
import { getPriceBoundsFromHits, getPriceBoundsFromRefinementValues } from './price-bounds';

type PriceBounds = { minAllowed?: number; maxAllowed?: number };

function getPriceRangeFromSearch(search: string): { min: string; max: string } {
    const params = new URLSearchParams(search);
    const priceRefine = params.getAll('refine').find((refine) => refine.startsWith('price='));
    if (!priceRefine) return { min: '', max: '' };

    const match = priceRefine.match(/price=\(([^.]*)\.\.([^)]*)\)/);
    if (!match) return { min: '', max: '' };

    return { min: match[1] || '', max: match[2] || '' };
}

function buildPriceRangeString(minPrice: string, maxPrice: string): string {
    return `(${minPrice || '0'}..${maxPrice || ''})`;
}

function isValidBounds(bounds: PriceBounds): bounds is { minAllowed: number; maxAllowed: number } {
    return bounds.minAllowed !== undefined && bounds.maxAllowed !== undefined;
}

function resolvePriceBounds(
    values: RefinementProps['values'],
    result?: ShopperSearch.schemas['ProductSearchResult']
): PriceBounds {
    const fromValues = getPriceBoundsFromRefinementValues(values);
    if (isValidBounds(fromValues)) {
        return fromValues;
    }

    return getPriceBoundsFromHits(result);
}

function useStablePriceBounds(
    pathname: string,
    values: RefinementProps['values'],
    result?: ShopperSearch.schemas['ProductSearchResult']
): PriceBounds {
    const [cachedBounds, setCachedBounds] = useState<{ minAllowed: number; maxAllowed: number } | undefined>(undefined);

    useEffect(() => {
        setCachedBounds(undefined);
    }, [pathname]);

    const resolvedBounds = useMemo(() => resolvePriceBounds(values, result), [result, values]);

    useEffect(() => {
        if (!cachedBounds && isValidBounds(resolvedBounds)) {
            setCachedBounds({
                minAllowed: resolvedBounds.minAllowed,
                maxAllowed: resolvedBounds.maxAllowed,
            });
        }
    }, [cachedBounds, resolvedBounds]);

    return cachedBounds ?? resolvedBounds;
}

export default function RefinePrice({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    result,
}: RefinementProps & {
    result?: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement {
    const location = useLocation();
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const { minAllowed, maxAllowed } = useStablePriceBounds(location.pathname, values, result);

    useEffect(() => {
        const { min, max } = getPriceRangeFromSearch(location.search);
        setMinPrice(min);
        setMaxPrice(max);
    }, [location.search]);

    const validation = usePriceRangeValidation(minPrice, maxPrice, minAllowed, maxAllowed);

    const applyPriceFilter = useCallback(
        (min: string, max: string) => {
            if (min || max) {
                toggleFilter(attributeId, buildPriceRangeString(min, max));
                return;
            }

            const urlRange = getPriceRangeFromSearch(location.search);
            if (urlRange.min || urlRange.max) {
                toggleFilter(attributeId, buildPriceRangeString(urlRange.min, urlRange.max));
            }
        },
        [attributeId, location.search, toggleFilter]
    );

    const handlePriceChange = (newMinPrice: string, newMaxPrice: string) => {
        setMinPrice(newMinPrice);
        setMaxPrice(newMaxPrice);
    };

    const handleApplyPriceFilter = () => {
        if (validation.minHasError || validation.maxHasError) {
            return;
        }

        applyPriceFilter(minPrice, maxPrice);
    };

    const handleSliderCommit = (nextMinPrice: string, nextMaxPrice: string) => {
        setMinPrice(nextMinPrice);
        setMaxPrice(nextMaxPrice);

        const commitValidation = validatePriceRange(nextMinPrice, nextMaxPrice, minAllowed, maxAllowed);
        if (commitValidation.minHasError || commitValidation.maxHasError) {
            return;
        }

        applyPriceFilter(nextMinPrice, nextMaxPrice);
    };

    return (
        <div className="flex flex-col gap-3">
            <PriceRangeInput
                minPrice={minPrice}
                maxPrice={maxPrice}
                onChange={handlePriceChange}
                onApply={handleApplyPriceFilter}
                onSliderCommit={handleSliderCommit}
                minAllowed={minAllowed}
                maxAllowed={maxAllowed}
                showValidationErrors={true}
            />

            {values.length > 0 && (
                <DefaultRefinement
                    values={values}
                    attributeId={attributeId}
                    isFilterSelected={isFilterSelected}
                    toggleFilter={toggleFilter}
                />
            )}
        </div>
    );
}
